import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { DisplayedColumn } from '../../../../core/models/misc/displayed-column.model';
import { TablePersonalizationDialogComponent } from '../../../../shared/dialogs/table-personalization-dialog/table-personalization-dialog.component';
import { DialogService } from '../dialog/dialog.service';
import { LocalStorageService } from '../local-storage/local-storage.service';
import {
    TablePersonalizationDefinition,
    TablePersonalizationDefinitions
} from '../../../../core/helpers/TablePersonalizationDefinitions';
import { ServiceModule } from '../../../service.module';
import {UisService} from "../uis/uis.service";
import {SwaService} from "../swa/swa.service";
import {DialogRef} from "@fundamental-ngx/core/dialog";

export interface TablePersonalizationItem {
	tableKey: string;
	defaultColumns: DisplayedColumn[];
	columnsSubject: BehaviorSubject<DisplayedColumn[]>;
}
@Injectable({ providedIn: ServiceModule })
export class TablePersonalizationService {
    tableColumns: Map<string, TablePersonalizationItem> = new Map();
    public readonly LocalStorageBaseKey = 'table-settings';

    constructor(private _dialogService: DialogService, private _localStorageService: LocalStorageService, private _uisService: UisService, protected swaService: SwaService) {
        this.addTableDefinitions(TablePersonalizationDefinitions.Definitions);
    }

    addTableDefinitions(definitions: TablePersonalizationDefinition[] = []) {
        definitions.forEach((definition) => {
            let tableKey = definition.key;
            if (this.tableColumns.has(tableKey)) {
                return;
            }
            let columns = definition.defaultColumns;
            let existingSettings = this.getExistingSettings(tableKey);
            if (existingSettings) {
                columns = this.alignExistingSettings(columns, existingSettings);
            }
            this.tableColumns.set(tableKey, {
                tableKey: tableKey,
                defaultColumns: definition.defaultColumns,
                columnsSubject: new BehaviorSubject<DisplayedColumn[]>(columns),
            });
        });
    }

    alignExistingSettings(columns: DisplayedColumn[], existingSettings: DisplayedColumn[]) {
        let columnsArray = columns.map((item: DisplayedColumn) => Object.assign(new DisplayedColumn(), item));
        columnsArray.sort((itemA, itemB) => {
            let aIndex = existingSettings.findIndex((a) => a.key === itemA.key);
            let bIndex = existingSettings.findIndex((b) => b.key === itemB.key);
            if (aIndex === -1 || bIndex === -1) {
                return 1;
            }
            return aIndex - bIndex;
        });
        columnsArray = columnsArray.map((item) => {
            let item2 = existingSettings.find((i2) => i2.key === item.key);
            item.checked = item2?.checked || false;
            return item;
        });
        return columnsArray;
    }

    apply(tableKey: string, columns: DisplayedColumn[]) {
        let subject = this.getTableSubject(tableKey);
        this._localStorageService.set(this.createLocalStorageKey(tableKey), JSON.stringify(columns));
        subject.columnsSubject.next(columns);
        this._uisService.sendUISEvent(this._uisService.UIS_EVENT_TYPES.SAVE_CONFIGURE_TABLE_COLUMNS,
            {columns: (columns.filter(column => column.checked)).map(item => item.key).join(",")});
        this.swaService.sendCustomEvent(this.swaService.SWA_EVENT_TYPES.CONFIGURE_TABLE_COLUMNS,
            [(columns.filter(column => column.checked)).map(item => item.key).join(", ")], []);
    }

    getTableSubject(tableKey: string): TablePersonalizationItem {
        let item = this.tableColumns.get(tableKey);
        if (!item) {
            throw new Error('Customizations for table is not defined');
        }
        return item;
    }

    createLocalStorageKey(tableKey: string): string {
        return `${this.LocalStorageBaseKey}-${tableKey}`;
    }

    getExistingSettings(tableKey: string): DisplayedColumn[] {
        let key = this.createLocalStorageKey(tableKey);
        let existing = this._localStorageService.get(key);
        if (existing) {
            return JSON.parse(existing);
        }
        return null;
    }

    openTablePersonalization(tableKey: string): DialogRef {
        let subject = this.getTableSubject(tableKey);
        const dialogRef = this._dialogService.open(TablePersonalizationDialogComponent, {
            width: '25rem',
            backdropClass: 'dialog-backdrop',
            verticalPadding: false,
            data: {
                columns: subject.columnsSubject.getValue(),
                defaultColumns: subject.defaultColumns,
            },
            disablePaddings: true
        });

        dialogRef.afterClosed.subscribe({
            next: (columns: DisplayedColumn[]) => {
                this.apply(tableKey, columns);
            },
            error: () => {},
        });

        this._uisService.sendEvent(this._uisService.UIS_EVENT_TYPES.OPEN_CONFIGURE_TABLE_COLUMNS);
        return dialogRef;
    }
}
